'use client'

import { cn } from '@heroui/react'
import { useEffect, useRef, useState } from 'react'

interface Stat {
  value: string
  label: string
}

const STATS: Stat[] = [
  { value: '200+', label: 'Fleet Jobs' },
  { value: '10+', label: 'Years Operating' },
  { value: '3,000', label: 'Sq Ft Facility' },
]

interface ClientLogo {
  src: string
  alt: string
  className?: string
}

const CLIENT_LOGOS: ClientLogo[] = [
  { src: '/images/clients/fedex.png', alt: 'FedEx', className: 'max-h-5 sm:max-h-6' },
  { src: '/images/clients/park-paving.png', alt: 'Park Paving' },
  { src: '/images/clients/grid-iron.png', alt: 'GridIron Drilling Services' },
  { src: '/images/clients/empire-envelope.png', alt: 'Empire Envelope' },
  { src: '/images/clients/911-restoration.png', alt: '911 Restoration' },
]

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false)
  useEffect(() => {
    if (!ref.current) return
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      setInView(true)
      return
    }
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true) },
      { threshold: 0.3 }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref])
  return inView
}

export function TrustBar() {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)

  return (
    <section ref={ref} className="w-full border-t border-border bg-surface">
      <div className="mx-auto max-w-[1200px] px-6 py-12">
        {/* Stats */}
        <div className="mb-6 grid grid-cols-3 gap-4 text-center">
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              className={cn(inView ? 'animate-[count-fade-in_0.5s_ease-out_forwards]' : 'opacity-0')}
              style={{ animationDelay: inView ? `${i * 150}ms` : undefined }}
            >
              <div className="text-2xl font-bold text-foreground sm:text-3xl">
                {stat.value}
              </div>
              <div className="cw-eyebrow mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Client logos */}
        <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-10">
          {CLIENT_LOGOS.map((logo) => (
            <div key={logo.alt} className="flex h-8 items-center sm:h-10">
              <img
                src={logo.src}
                alt={logo.alt}
                // Grayscale + dimmed per the design-system client-logo-strip
                // spec: five different brand palettes otherwise compete with
                // the page. Full colour + opacity on hover.
                className={`w-auto max-w-[120px] object-contain grayscale opacity-40 transition-[filter,opacity] duration-300 hover:grayscale-0 hover:opacity-100 sm:max-w-[140px] ${logo.className ?? 'h-full'}`}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
